/**
 * Reordering, as a function of a list rather than of a gesture.
 *
 * A drag and a keyboard press are two ways of saying the same thing — "this one goes
 * there" — so both end up here, and the list is the only state either of them
 * changes. That is what makes the keyboard path a first-class way to reorder rather
 * than a concession: it is not a fallback implementation, it is the same one.
 */

/** The list with the item at `from` set down at `to`. Out-of-range is a no-op. */
export function moveItem<T>(
  items: readonly T[],
  from: number,
  to: number,
): T[] {
  const next = [...items];
  if (from < 0 || from >= next.length) return next;
  const target = Math.max(0, Math.min(next.length - 1, to));
  if (target === from) return next;
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return [...items];
  next.splice(target, 0, moved);
  return next;
}

/**
 * The same move said relatively, which is what a keyboard says. The new index comes
 * back with the list because focus has to follow the item the operator is moving —
 * a reorder that leaves the focus behind moves the next press to the wrong row.
 */
export function moveBy<T>(
  items: readonly T[],
  index: number,
  delta: number,
): { items: T[]; index: number } {
  if (index < 0 || index >= items.length) {
    return { items: [...items], index };
  }
  const target = Math.max(0, Math.min(items.length - 1, index + delta));
  return { items: moveItem(items, index, target), index: target };
}

/** Whether two orderings of the same things differ. Identity is the item's id. */
export function orderChanged(
  before: readonly { id: string }[],
  after: readonly { id: string }[],
): boolean {
  if (before.length !== after.length) return true;
  return after.some((item, index) => before[index]?.id !== item.id);
}
